import { State, Action, StateContext } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import { catchError, tap } from 'rxjs';
import { NotificationService } from '../../app/services/notification.service';
import { ApiKeysStateModel } from './api-keys.model';
import { ApiKeysService } from './api-keys.service';
import { CreateApiKey, DeleteApiKey, GetAllApiKeys, UpdateApiKey } from './api-keys.actions';

@State<ApiKeysStateModel>({
  name: 'apiKeys',
  defaults: {
    apiKeys: [],
    pagination: {
      totalItems: 0,
      totalPages: 0,
      currentPage: 0,
      itemsPerPage: 0,
    },
    headers: [],
    isLoading: false,
  },
})
@Injectable({ providedIn: 'root' })
export class ApiKeysState {
  private readonly notificationService = inject(NotificationService);
  private readonly apiKeysService = inject(ApiKeysService);

  @Action(GetAllApiKeys)
  getAllApiKeys(ctx: StateContext<ApiKeysStateModel>) {
    ctx.patchState({ isLoading: true });
    return this.apiKeysService.getAllApiKeys().pipe(
      tap((res) =>
        ctx.patchState({
          apiKeys: res.payload,
          pagination: res.pagination,
          headers: res.headers,
          isLoading: false,
        }),
      ),
      catchError(async (_error) => {
        ctx.patchState({ isLoading: false });
      }),
    );
  }

  @Action(CreateApiKey)
  createApiKey(ctx: StateContext<ApiKeysStateModel>, action: CreateApiKey) {
    ctx.patchState({ isLoading: true });
    return this.apiKeysService.createApiKey(action.payload).pipe(
      tap((res) => {
        ctx.patchState({ isLoading: false });
        this.notificationService.openSuccessToast('API key created successfully!');
      }),
      catchError(async (_error) => {
        ctx.patchState({ isLoading: false });
      }),
    );
  }

  @Action(DeleteApiKey)
  deleteApiKey(ctx: StateContext<ApiKeysStateModel>, action: DeleteApiKey) {
    ctx.patchState({ isLoading: true });
    return this.apiKeysService.deleteApiKey(action.id).pipe(
      tap((res) => {
        ctx.patchState({ isLoading: false });
        this.notificationService.openSuccessToast('API key deleted successfully!');
      }),
      catchError(async (_error) => {
        ctx.patchState({ isLoading: false });
      }),
    );
  }

  @Action(UpdateApiKey)
  updateApiKey(ctx: StateContext<ApiKeysStateModel>, action: UpdateApiKey) {
    ctx.patchState({ isLoading: true });
    return this.apiKeysService.updateApiKey(action.id, action.payload).pipe(
      tap((res) => {
        ctx.patchState({ isLoading: false });
        this.notificationService.openSuccessToast('API key updated successfully!');
      }),
      catchError(async (_error) => {
        ctx.patchState({ isLoading: false });
      }),
    );
  }
}
